import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { analyticsControllerGetTenantHome } from '@/generated/korza-api-client';
import type { TenantHomeResponseDto } from '@/generated/korza-api-client/types.gen';
import type { ChartGranularity } from '@/lib/chart-axis';
import { useTenant } from '@/hooks/use-tenant';

export interface TenantHomeParams {
  fromDate: string;
  toDate: string;
  granularity: ChartGranularity;
}

/**
 * Query options of `GET tenants/:slug/analytics/home` (same key as before),
 * so the current and previous periods, the KPI tiles and the revenue card
 * share the cache.
 */
export function tenantHomeQueryOptions(
  tenantSlug: string,
  { fromDate, toDate, granularity }: TenantHomeParams
) {
  return {
    queryKey: ['metrics-home', tenantSlug, fromDate, toDate, granularity],
    queryFn: async (): Promise<TenantHomeResponseDto> => {
      const response = await analyticsControllerGetTenantHome({
        path: { tenantSlug },
        query: { fromDate, toDate, granularity },
        throwOnError: true,
      });
      return response.data;
    },
  };
}

/** `GET tenants/:slug/analytics/home` for the Início (same key as before). */
export function useTenantHome(
  params: TenantHomeParams
): UseQueryResult<TenantHomeResponseDto, Error> {
  const tenant = useTenant();
  return useQuery(tenantHomeQueryOptions(tenant.slug, params));
}
